<template>
  <PForm nested :schema="v$" :validate-on="['blur', 'input']">
    <section class="primary-lighten-1 mt-2 w-full rounded-md p-2" :aria-labelledby="`${id}-title`">
      <h3 :id="`${id}-title`" class="mb-2 text-xs font-semibold select-none">Facturatie</h3>
      <div class="gray-lighten-2 mb-2 space-y-3 rounded-md p-2">
        <h4 class="text-xs font-semibold select-none">Adres en verzending</h4>
        <PCheckbox
          v-model="invoiceDraft.useDifferentAddress"
          size="sm"
          label="Afwijkend factuuradres"
        />
        <p v-if="!invoiceDraft.useDifferentAddress" class="text-xs">
          Het bedrijfsadres wordt gebruikt.
        </p>
        <div v-else class="space-y-3">
          <label v-for="field in addressFields" :key="field.key" class="block">
            <span class="mb-1.5 block text-xs font-semibold select-none">{{ field.label }}</span>
            <PInput v-model="invoiceDraft.address[field.key]" :aria-label="field.label" />
          </label>
        </div>
        <label class="block">
          <span class="mb-1.5 block text-xs font-semibold select-none">E-mailadres facturen</span>
          <PTextarea
            v-model="invoiceDraft.emails"
            :rows="2"
            :color="v$.emails.$errors.length ? 'error' : 'secondary'"
            @blur="v$.emails.$touch()"
            aria-label="E-mailadres facturen"
          />
          <p v-if="v$.emails.$errors.length" class="text-error mt-2 text-sm">
            {{ v$.emails.$errors[0] }}
          </p>
        </label>
        <p class="text-xs">Voer e-mailadressen in, gescheiden door puntkomma's.</p>
      </div>
      <div class="gray-lighten-2 mb-2 space-y-3 rounded-md p-2">
        <h4 class="text-xs font-semibold select-none">Btw en betaling</h4>
        <label class="block">
          <span class="mb-1.5 block text-xs font-semibold select-none">Btw-behandeling</span>
          <InputSelect
            v-model="selectedVatTreatment"
            :items="vatTreatments"
            value-key="key"
            label-key="label"
            aria-label="Btw-behandeling"
          />
        </label>
        <label v-if="invoiceDraft.reverseCharge" class="block">
          <span class="mb-1.5 block text-xs font-semibold select-none"
            >Normaal toepasselijk tarief</span
          >
          <InputSelect
            v-model="selectedReverseChargeRate"
            :items="reverseChargeRates"
            value-key="key"
            label-key="label"
            aria-label="Normaal toepasselijk tarief"
          />
          <span class="mt-1.5 block text-xs">
            De klant berekent de btw tegen dit tarief. Je brengt zelf geen btw in rekening.
          </span>
        </label>
        <label class="block">
          <span class="mb-1.5 block text-xs font-semibold select-none"
            >Btw-identificatienummer klant</span
          >
          <PInput
            v-model="invoiceDraft.customerVatId"
            :color="v$.customerVatId.$error ? 'error' : 'secondary'"
            @blur="v$.customerVatId.$touch()"
            aria-label="Btw-identificatienummer klant"
          />
        </label>
        <p v-if="invoiceDraft.reverseCharge" class="text-xs">
          Bij btw verlegd is het btw-identificatienummer van de klant verplicht.
        </p>
        <label class="block">
          <span class="mb-1.5 block text-xs font-semibold select-none">Betaaltermijn (dagen)</span>
          <InputNumber
            v-model="paymentTermDays"
            natural-numbers-only
            allow-zero
            :min-decimals="0"
            :errors="v$.paymentTermDays.$errors"
            @blur="v$.paymentTermDays.$touch()"
            inputmode="numeric"
            aria-label="Betaaltermijn (dagen)"
          />
        </label>
        <label class="block">
          <span class="mb-1.5 block text-xs font-semibold select-none">G-rekening (%)</span>
          <InputNumber
            v-model="gAccountPercentage"
            allow-zero
            :min-decimals="0"
            :errors="v$.gAccountPercentage.$errors"
            @blur="v$.gAccountPercentage.$touch()"
            inputmode="decimal"
            aria-label="G-rekening (%)"
          />
        </label>
        <p class="text-xs">
          Standaardwaarden voor nieuwe facturen. Btw en G-rekeningpercentage zijn op de factuur
          aanpasbaar.
        </p>
      </div>
      <div class="space-y-2">
        <section
          v-for="part in invoiceParts"
          :key="part.key"
          class="gray-lighten-2 min-w-0 space-y-3 rounded-md p-2"
          :aria-labelledby="`${id}-${part.key}`"
        >
          <div>
            <h4 :id="`${id}-${part.key}`" class="text-xs font-semibold select-none">
              {{ part.title }}
            </h4>
            <p class="mt-1 text-xs">{{ part.description }}</p>
          </div>
          <PCheckbox
            v-model="invoiceDraft[part.key].approvalAttachments"
            size="sm"
            label="Akkoordbijlage"
          />
          <PCheckbox
            :model-value="invoiceDraft[part.key].productionStatementMode === 'without_attachments'"
            @update:model-value="
              (selected) =>
                (invoiceDraft[part.key].productionStatementMode = selected
                  ? 'without_attachments'
                  : null)
            "
            size="sm"
            label="Productieverantwoording zonder bijlagen"
          />
          <PCheckbox
            :model-value="invoiceDraft[part.key].productionStatementMode === 'with_attachments'"
            @update:model-value="
              (selected) =>
                (invoiceDraft[part.key].productionStatementMode = selected
                  ? 'with_attachments'
                  : null)
            "
            size="sm"
            label="Productieverantwoording met bijlagen"
          />
          <PCheckbox
            v-model="invoiceDraft[part.key].manDaysRegister"
            size="sm"
            label="Mandagenregister"
          />
          <p v-if="part.key === 'attachment' && !hasInvoiceAttachment" class="text-xs">
            Niets aangevinkt: geen aparte bijlage.
          </p>
        </section>
      </div>
      <div class="gray-lighten-2 mt-2 space-y-3 rounded-md p-2">
        <h4 class="text-xs font-semibold select-none">Limieten</h4>
        <label class="block">
          <span class="mb-1.5 block text-xs font-semibold select-none"
            >Maximale bestandsgrootte (kB)</span
          >
          <InputNumber
            v-model="maxFileSizeKb"
            natural-numbers-only
            :min-decimals="0"
            :errors="v$.maxFileSizeKb.$errors"
            @blur="v$.maxFileSizeKb.$touch()"
            aria-label="Maximale bestandsgrootte (kB)"
          />
        </label>
        <label class="block">
          <span class="mb-1.5 block text-xs font-semibold select-none"
            >Maximaal aantal bijlagen</span
          >
          <InputNumber
            v-model="maxAttachments"
            natural-numbers-only
            :min-decimals="0"
            :errors="v$.maxAttachments.$errors"
            @blur="v$.maxAttachments.$touch()"
            aria-label="Maximaal aantal bijlagen"
          />
        </label>
        <label class="block">
          <span class="mb-1.5 block text-xs font-semibold select-none">Maximaal bedrag (€)</span>
          <InputNumber
            v-model="maxAmount"
            :min-decimals="2"
            :errors="v$.maxAmount.$errors"
            @blur="v$.maxAmount.$touch()"
            aria-label="Maximaal bedrag (€)"
          />
        </label>
      </div>
      <div class="gray-lighten-2 mt-2 space-y-2 rounded-md p-2">
        <label class="block">
          <span class="mb-1.5 block text-xs font-semibold select-none">Interne opmerking</span>
          <PTextarea v-model="invoiceDraft.internalNote" :rows="3" aria-label="Interne opmerking" />
        </label>
        <p class="text-xs">
          Alleen voor intern gebruik. Komt niet op de factuur of in de klantmail.
        </p>
      </div>
    </section>
  </PForm>
</template>

<script setup lang="ts">
import { computed, useId } from "vue"
import type { CustomerInvoicingSettings } from "@/interfaces/api/customer-invoicing-settings.interface"
import { useRegle } from "@regle/core"
import { withMessage, requiredIf } from "@regle/rules"
import { multipleEmailsValidator } from "@/helper/validation/validators"
import PForm from "@/components/form/PForm.vue"
import PCheckbox from "@/components/form/PCheckbox.vue"
import PInput from "@/components/form/PInput.vue"
import PTextarea from "@/components/form/PTextarea.vue"
import InputNumber from "@/components/form/PInputNumber.vue"
import InputSelect from "@/components/form/PSelect.vue"

const id = useId()
const invoiceParts = [
  { key: "invoice", title: "Factuur", description: "In het factuurbestand" },
  { key: "attachment", title: "Bijlagen", description: "In de aparte bijlage" }
] as const
const addressFields = [
  { key: "attention", label: "Afdeling / t.a.v. (optioneel)" },
  { key: "street", label: "Straat en huisnummer" },
  { key: "postalCode", label: "Postcode" },
  { key: "city", label: "Plaats" },
  { key: "country", label: "Land" }
] as const
const vatTreatments = [
  { key: "21", label: "21% btw", rate: 21, reverseCharge: false, exempt: false },
  { key: "9", label: "9% btw", rate: 9, reverseCharge: false, exempt: false },
  { key: "0", label: "0% btw", rate: 0, reverseCharge: false, exempt: false },
  { key: "exempt", label: "Vrijgesteld", rate: 0, reverseCharge: false, exempt: true },
  { key: "reverse", label: "Btw verlegd", rate: null, reverseCharge: true, exempt: false }
]
const reverseChargeRates = [
  { key: 21, label: "21%" },
  { key: 9, label: "9%" }
]
const invoiceDraft = defineModel<CustomerInvoicingSettings>({ required: true })
const selectedVatTreatment = computed({
  get: () => {
    const settings = invoiceDraft.value
    const key = settings.vatExempt
      ? "exempt"
      : settings.reverseCharge
        ? "reverse"
        : String(settings.vatRate)
    return key
  },
  set: (key) => {
    const treatment = vatTreatments.find((item) => item.key === key)!
    invoiceDraft.value.vatRate = treatment.reverseCharge
      ? invoiceDraft.value.vatRate === 9
        ? 9
        : 21
      : (treatment.rate as 0 | 9 | 21)
    invoiceDraft.value.reverseCharge = treatment.reverseCharge
    invoiceDraft.value.vatExempt = treatment.exempt
  }
})
const selectedReverseChargeRate = computed({
  get: () => invoiceDraft.value.vatRate,
  set: (rate) => {
    invoiceDraft.value.vatRate = rate as 9 | 21
  }
})
// Empty inputs are stored as null, so "not configured" stays distinct from zero.
function integerInput(key: "paymentTermDays" | "maxFileSizeKb" | "maxAttachments") {
  return computed({
    get: () => invoiceDraft.value[key] ?? "",
    set: (value: string | number) => {
      invoiceDraft.value[key] = value === "" ? null : Number(value)
    }
  })
}
function decimalInput(key: "gAccountPercentage" | "maxAmount") {
  return computed({
    get: () => invoiceDraft.value[key] ?? "",
    set: (value: string | number) => {
      invoiceDraft.value[key] = value === "" ? null : String(value)
    }
  })
}
const paymentTermDays = integerInput("paymentTermDays")
const maxFileSizeKb = integerInput("maxFileSizeKb")
const maxAttachments = integerInput("maxAttachments")
const gAccountPercentage = decimalInput("gAccountPercentage")
const maxAmount = decimalInput("maxAmount")
const hasInvoiceAttachment = computed(() => {
  const attachment = invoiceDraft.value.attachment
  return (
    attachment.productionStatementMode !== null ||
    attachment.approvalAttachments ||
    attachment.manDaysRegister
  )
})
const rules = computed(() => ({
  emails: { multipleEmailsValidator },
  customerVatId: {
    required: withMessage(
      requiredIf(invoiceDraft.value.reverseCharge),
      "Vul bij btw verlegd het btw-identificatienummer in."
    )
  },
  gAccountPercentage: {
    range: withMessage(
      (value: string | null | undefined) =>
        value == null || value === "" || (Number(value) >= 0 && Number(value) <= 100),
      "Gebruik een percentage van 0–100."
    ),
    decimals: withMessage(validDecimal, "Gebruik maximaal 2 decimalen.")
  },
  paymentTermDays: {
    range: withMessage(
      (value: number | null | undefined) =>
        value == null || (Number.isInteger(value) && value >= 0),
      "Gebruik hele dagen vanaf 0."
    )
  },
  maxFileSizeKb: {
    range: withMessage(positiveInteger, "Gebruik een geheel getal vanaf 1.")
  },
  maxAttachments: {
    range: withMessage(positiveInteger, "Gebruik een geheel getal vanaf 1.")
  },
  maxAmount: {
    range: withMessage(
      (value: string | null | undefined) => value == null || value === "" || Number(value) > 0,
      "Gebruik een bedrag groter dan 0."
    ),
    decimals: withMessage(validDecimal, "Gebruik maximaal 2 decimalen.")
  }
}))
function positiveInteger(value: number | null | undefined) {
  return value == null || (Number.isInteger(value) && value >= 1)
}
function validDecimal(value: string | null | undefined) {
  return value == null || value === "" || /^\d+(\.\d{1,2})?$/.test(value!)
}
const { r$: v$ } = useRegle(invoiceDraft, rules, { autoDirty: false })
</script>
